import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useCartStore } from '../context/CartStore';
import { FaStar, FaArrowLeft, FaTruck, FaShieldAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import AuthModal from '../components/AuthModal';
import useProtectedAction from '../hooks/useProtectedAction';
import { motion, AnimatePresence } from 'framer-motion';

const ProductDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeImage, setActiveImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState('');
  const [customizationText, setCustomizationText] = useState('');

  const addItem = useCartStore((state) => state.addItem);
  const { executeAction, authModalOpen, setAuthModalOpen, actionType } = useProtectedAction();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/products/${id}`);
        setProduct(response.data);
        setError('');
      } catch (err) {
        setError('Failed to load product details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, api]);

  const handleAddToCart = () => {
    executeAction(() => {
      if (product.sizes?.length > 0 && !selectedSize) {
        toast.error('Please specify a size required for acquisition.');
        return;
      }
      const cartItem = { ...product, selectedSize, customizationText };
      for (let i = 0; i < quantity; i++) addItem(cartItem);
      toast.success(`Successfully added to your private collection`);
    }, 'cart');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-deepDark flex flex-col items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-textMuted text-xs tracking-widest uppercase">Retrieving details...</p>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-deepDark flex flex-col items-center justify-center px-4">
        <div className="text-lg font-light text-white mb-8">{error || 'Piece no longer available'}</div>
        <button onClick={() => navigate('/products')} className="flex items-center gap-3 text-gold hover:text-white transition-colors text-xs tracking-widest uppercase">
          <FaArrowLeft /> Return to Collection
        </button>
      </div>
    );
  }

  const discountedPrice = product.discount > 0 ? product.price * (1 - product.discount / 100) : product.price;

  return (
    <>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="min-h-screen bg-deepDark pt-10 pb-20 px-4"
      >
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <Link to="/products" className="text-textMuted hover:text-gold flex items-center gap-3 text-xs tracking-widest uppercase w-fit transition-colors">
              <FaArrowLeft /> Collection
            </Link>
          </div>

          <div className="flex flex-col lg:flex-row gap-12 xl:gap-20">
            {/* Left: Image Gallery */}
            <div className="w-full lg:w-1/2 flex flex-col gap-6">
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
                className="relative aspect-square w-full bg-[#0a0a0a] border border-white/5 flex items-center justify-center overflow-hidden group"
              >
                {product.images?.length > 0 ? (
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={activeImage}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      src={product.images[activeImage]}
                      alt={product.name}
                      className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-500"
                    />
                  </AnimatePresence>
                ) : (
                  <div className="text-textMuted text-xs tracking-widest uppercase">Image Unavailable</div>
                )}
                {product.discount > 0 && (
                  <span className="absolute top-6 left-6 bg-gold text-deepDark px-4 py-2 font-bold text-[10px] tracking-widest uppercase">
                    -{product.discount}% Collection
                  </span>
                )}
              </motion.div>

              {/* Thumbnails */}
              {product.images?.length > 1 && (
                <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
                  {product.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveImage(idx)}
                      className={`relative w-24 h-24 flex-shrink-0 transition-all duration-300 ${activeImage === idx ? 'border border-gold brightness-100' : 'border border-white/10 brightness-50 hover:brightness-100'}`}
                    >
                      <img src={img} alt="Thumbnail view" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Product Details */}
            <div className="w-full lg:w-1/2 flex flex-col">
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="text-4xl md:text-5xl font-serif text-white mb-4 leading-tight tracking-wide"
              >
                {product.name}
              </motion.h1>

              <div className="flex items-center gap-6 mb-10 pb-10 border-b border-white/10">
                <div className="flex items-center gap-2">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} className={`text-xs ${i < Math.floor(product.rating || 0) ? 'text-gold' : 'text-white/20'}`} />
                  ))}
                  <span className="text-xs text-textMuted tracking-widest uppercase ml-2">({product.reviews?.length || 0})</span>
                </div>
                <span className="w-[1px] h-4 bg-white/20"></span>
                <span className="text-[10px] font-bold tracking-widest uppercase text-white border border-white/20 px-3 py-1">
                  {product.material}
                </span>
                <span className="text-[10px] font-bold tracking-widest uppercase text-textMuted border border-white/5 px-3 py-1">
                  {product.category}
                </span>
              </div>

              <div className="mb-10">
                <div className="flex items-baseline gap-4 mb-2">
                  <span className="text-4xl font-serif text-gold">₹{discountedPrice.toLocaleString()}</span>
                  {product.discount > 0 && (
                    <span className="text-lg text-textMuted line-through font-light">₹{product.price.toLocaleString()}</span>
                  )}
                </div>
                <p className="text-xs text-white/40 tracking-widest uppercase">Duties & Taxes Included</p>
              </div>

              <div className="mb-12">
                <p className="text-textMuted font-light leading-relaxed text-sm">{product.description}</p>
              </div>

              {/* Attributes */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-y-8 gap-x-4 mb-12">
                {product.weight && (
                  <div>
                    <span className="block text-[10px] text-textMuted tracking-widest uppercase mb-2">Weight</span>
                    <span className="text-sm text-white font-light">{product.weight}</span>
                  </div>
                )}
                {product.purity && (
                  <div>
                    <span className="block text-[10px] text-textMuted tracking-widest uppercase mb-2">Purity</span>
                    <span className="text-sm text-white font-light">{product.purity}</span>
                  </div>
                )}
                {product.certification && (
                  <div>
                    <span className="block text-[10px] text-textMuted tracking-widest uppercase mb-2">Certificate</span>
                    <span className="text-sm text-white font-light">{product.certification}</span>
                  </div>
                )}
              </div>

              {/* Selections */}
              <div className="mb-12 space-y-8">
                {product.sizes?.length > 0 && (
                  <div>
                    <label className="block text-[10px] tracking-widest uppercase text-white mb-4">Select Dimension</label>
                    <div className="flex flex-wrap gap-4">
                      {product.sizes.map((size) => (
                        <button
                          key={size}
                          onClick={() => setSelectedSize(size)}
                          className={`w-12 h-12 flex items-center justify-center text-xs tracking-wider transition-all duration-300 ${selectedSize === size ? 'bg-gold text-deepDark font-bold' : 'bg-transparent border border-white/20 text-textMuted hover:border-gold hover:text-white'}`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {product.customization && (
                  <div>
                    <label className="block text-[10px] tracking-widest uppercase text-white mb-4">Complimentary Engraving</label>
                    <input
                      type="text"
                      className="w-full bg-surfaceDark border-b border-white/20 px-0 py-3 text-white focus:outline-none focus:border-gold transition-colors font-light text-sm placeholder:text-white/20"
                      placeholder="Add an initial, date, or message..."
                      value={customizationText}
                      onChange={(e) => setCustomizationText(e.target.value)}
                    />
                  </div>
                )}
              </div>

              {/* Acquisition Logic */}
              <div className="mt-auto border-t border-white/10 pt-10">
                <div className="mb-8">
                  {product.stock > 0 ? (
                    <span className="text-xs text-white/50 tracking-widest uppercase">
                      <span className="text-gold mr-2">✦</span>{product.stock} Pieces Available in Vault
                    </span>
                  ) : (
                    <span className="text-xs text-red-500/80 tracking-widest uppercase">Waitlist Open</span>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-6 mb-12">
                  <div className="flex items-center justify-between border border-white/20 w-full sm:w-32 h-14 px-4 bg-surfaceDark">
                    <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="text-white/50 hover:text-white transition-colors p-2" disabled={product.stock === 0}>-</button>
                    <span className="font-serif text-white text-lg">{quantity}</span>
                    <button onClick={() => setQuantity(Math.min(product.stock, quantity + 1))} className="text-white/50 hover:text-white transition-colors p-2" disabled={product.stock === 0 || quantity >= product.stock}>+</button>
                  </div>

                  <button
                    className="flex-1 bg-gold text-deepDark font-bold text-xs tracking-widest uppercase h-14 hover:bg-white hover:text-deepDark transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
                    onClick={handleAddToCart}
                    disabled={product.stock === 0}
                  >
                    Acquire Selection
                  </button>
                </div>

                {/* Information Badges */}
                <div className="flex flex-col sm:flex-row gap-8">
                  <div className="flex items-start gap-4 flex-1">
                    <FaTruck className="text-gold text-xl" />
                    <div>
                      <p className="font-bold tracking-widest uppercase text-[10px] text-white mb-1">White-Glove Delivery</p>
                      <p className="text-xs font-light text-textMuted">Insured & tracked logistics globally.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 flex-1">
                    <FaShieldAlt className="text-gold text-xl" />
                    <div>
                      <p className="font-bold tracking-widest uppercase text-[10px] text-white mb-1">Authenticity Guaranteed</p>
                      <p className="text-xs font-light text-textMuted">Accompanied by verifiable certification.</p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </motion.div>

      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} actionType={actionType} />
    </>
  );
};

export default ProductDetailsPage;
